// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// View / panel navigation controller: the runtime view-switching behavior that
// setup-navigation.js wires the toolbar tabs and hash routing to.
//
// - activateView / activateAircraftWorkspaceShell: switch the top-level app view
//   (dashboard, flights, trends, home pages, ...) and lazily load flight data.
// - viewFromLocationHash / activateHashView / aircraftPanelFromLocationHash:
//   URL-hash <-> view/panel routing.
// - setReportPanel / activateDashboard*Panel / reportPanelToggleGroups: the
//   dashboard report sub-tabs (overview / AI review / explorer ...).
// - setAircraftPanel / mountAircraftDashboardPanel / syncAircraftPanelState: the
//   aircraft workspace sub-panels (profile / flights / uploads / trends ...).
// - trackCurrentAnalyticsPageView: page-view analytics for the active state.
//
// Everything else is injected: render/load helpers as lambdas, shared app state
// (selected flight/aircraft-panel, health summary, source monitor) via accessors.

import { analyticsPathForAppState, trackAnalyticsPageView } from "./analytics.js";
import { perfFlush, perfTime } from "../core/perf-utils.js";
import { syncAircraftTabNewDots } from "./new-item-marks.js";

const flightReviewPanelRouteSlugs = Object.freeze({
  overview: "overview",
  "ai-review": "ai-summary",
  engine: "engine",
  explorer: "graph-explorer",
  profile: "flight-profile",
  raw: "raw-data",
  playback: "playback",
});

const flightReviewRoutePanels = Object.freeze(Object.fromEntries(
  Object.entries(flightReviewPanelRouteSlugs).flatMap(([panel, slug]) => (
    slug === panel ? [[slug, panel]] : [[slug, panel], [panel, panel]]
  )),
));

const overviewHealthCardRouteSlugs = Object.freeze({
  overall: "overall",
  sensors: "sensors",
  turbo: "turbo-boost",
  oilSystem: "oil-system",
  coolantMargin: "coolant-margin",
  takeoffEnrichment: "takeoff-enrichment",
  cht: "cht-heat",
  egt: "egt-balance",
  valve: "egt-rhythm",
  combustion: "combustion",
  abnormalCombustion: "abnormal-combustion",
  divergence: "egt-cht-divergence",
  descentEgtDropout: "descent-egt-dropout",
  inductionLeak: "induction-leak",
  mag: "mag-check",
  cooling: "cooling",
  coolingRate: "cooling-rate",
  carbIce: "carb-ice",
  charging: "start-charge",
  electrical: "electrical",
  engineDelta: "engine-delta",
});

const overviewHealthCardRouteKeys = Object.freeze({
  ...Object.fromEntries(Object.entries(overviewHealthCardRouteSlugs).flatMap(([key, slug]) => [[slug, key], [key, key]])),
  "egt-stability": "valve",
  "valve": "valve",
  "turbo": "turbo",
  "turbo-boost": "turbo",
  "oil": "oilSystem",
  "oil-system": "oilSystem",
  "cht": "cht",
  "cht-heat": "cht",
  "egt": "egt",
  "egt-balance": "egt",
  "egt-rhythm": "valve",
  "start-charge": "charging",
});

const sensitivityRouteSlugs = Object.freeze({
  conservative: "low",
  balanced: "balanced",
  sensitive: "high",
});

const sensitivityRouteValues = Object.freeze({
  low: "conservative",
  conservative: "conservative",
  balanced: "balanced",
  high: "sensitive",
  sensitive: "sensitive",
});

export function normalizeFlightReviewPanel(panel) {
  return flightReviewPanelRouteSlugs[panel] ? panel : "overview";
}

export function normalizeOverviewHealthCardRouteKey(value) {
  const key = String(value ?? "").trim();
  return overviewHealthCardRouteKeys[key] ?? "";
}

export function normalizeRouteSensitivity(value) {
  const key = String(value ?? "").trim();
  return sensitivityRouteValues[key] ?? "";
}

function sensitivityRouteSlug(value) {
  const normalized = normalizeRouteSensitivity(value);
  const slug = normalized ? sensitivityRouteSlugs[normalized] : "";
  // The app default is balanced; leaving it out keeps shared links short and
  // behavior identical (parsing still accepts an explicit balanced).
  return slug === "balanced" ? "" : slug;
}

export function normalizeRouteZoom(value) {
  if (value && typeof value === "object") {
    const start = Number(value.start);
    const end = Number(value.end);
    if (!Number.isFinite(start) || !Number.isFinite(end) || start < 0 || end > 1 || end <= start) return null;
    return { start, end };
  }
  const match = String(value ?? "").trim().match(/^([0-9]+(?:\.[0-9]+)?|\.[0-9]+)-([0-9]+(?:\.[0-9]+)?|\.[0-9]+)$/);
  if (!match) return null;
  return normalizeRouteZoom({ start: Number(match[1]), end: Number(match[2]) });
}

function routeZoomValue(value) {
  const zoom = normalizeRouteZoom(value);
  if (!zoom || (zoom.start <= 0 && zoom.end >= 1)) return "";
  const format = (number) => Number(number.toFixed(4)).toString();
  return `${format(zoom.start)}-${format(zoom.end)}`;
}

export function normalizeRouteChannels(value) {
  if (value === null || value === undefined) return null;
  const explicitNone = !Array.isArray(value) && String(value).trim().toLowerCase() === "none";
  const raw = Array.isArray(value)
    ? value
    : explicitNone
      ? []
      : String(value).split(",");
  if (!raw.length) return Array.isArray(value) || explicitNone ? [] : null;
  const keys = [];
  const seen = new Set();
  raw.forEach((item) => {
    const key = String(item ?? "").trim();
    if (!/^[A-Za-z][A-Za-z0-9]*$/.test(key) || seen.has(key)) return;
    seen.add(key);
    keys.push(key);
  });
  return keys.length ? keys : null;
}

function routeChannelsValue(value) {
  const channels = normalizeRouteChannels(value);
  if (!channels) return "";
  return channels.length ? channels.join(",") : "none";
}

export function normalizeRouteFit(value) {
  if (value === true || value === false) return value;
  const key = String(value ?? "").trim().toLowerCase();
  if (["1", "true", "yes", "on", "fit"].includes(key)) return true;
  if (["0", "false", "no", "off", "normal"].includes(key)) return false;
  return null;
}

function routeFitValue(value) {
  const fit = normalizeRouteFit(value);
  return fit === null ? "" : fit ? "1" : "0";
}

function hashValueParts(value) {
  const hash = String(value ?? "").replace(/^#/, "").trim();
  const queryIndex = hash.indexOf("?");
  const path = queryIndex === -1 ? hash : hash.slice(0, queryIndex);
  const query = queryIndex === -1 ? "" : hash.slice(queryIndex + 1);
  return { parts: path.split("/"), params: new URLSearchParams(query) };
}

function routeStateFromArgs(sensitivityOrState, routeState = {}) {
  if (sensitivityOrState && typeof sensitivityOrState === "object" && !Array.isArray(sensitivityOrState)) {
    return sensitivityOrState;
  }
  return { ...routeState, sensitivity: sensitivityOrState };
}

function routeParamString(params) {
  return params
    .filter(([, value]) => value)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value).replace(/%2C/g, ",")}`)
    .join("&");
}

function appendRouteStateToHash(hash, state = {}) {
  const sensitivity = sensitivityRouteSlug(state.sensitivity);
  const zoom = Object.prototype.hasOwnProperty.call(state, "zoom") ? routeZoomValue(state.zoom) : "";
  const channels = Object.prototype.hasOwnProperty.call(state, "channels") ? routeChannelsValue(state.channels) : "";
  const fit = Object.prototype.hasOwnProperty.call(state, "fit") ? routeFitValue(state.fit) : "";
  const query = routeParamString([
    ["sensitivity", sensitivity],
    ["zoom", zoom],
    ["channels", channels],
    ["fit", fit],
  ]);
  return query ? `${hash}?${query}` : hash;
}

export function flightReviewRouteHash(flightId, panel = "overview", overviewHealthKey = "", sensitivity = "", routeState = {}) {
  const id = String(flightId ?? "").trim();
  if (!id) return "";
  const routePanel = normalizeFlightReviewPanel(panel);
  const healthKey = routePanel === "overview"
    ? normalizeOverviewHealthCardRouteKey(overviewHealthKey)
    : "";
  const healthSlug = healthKey && healthKey !== "overall"
    ? overviewHealthCardRouteSlugs[healthKey]
    : "";
  // Overview with no focused health card is the default; omitting it keeps
  // shared links short (#flights/<id> parses back to overview).
  const panelSegment = routePanel === "overview" && !healthSlug
    ? ""
    : `/${flightReviewPanelRouteSlugs[routePanel]}`;
  return appendRouteStateToHash(
    `#flights/${encodeURIComponent(id)}${panelSegment}${healthSlug ? `/${healthSlug}` : ""}`,
    routeStateFromArgs(sensitivity, routeState),
  );
}

export function parseFlightReviewHashValue(value) {
  const { parts, params } = hashValueParts(value);
  if (parts[0] !== "flights" || parts.length < 2) return null;
  let flightId = "";
  try {
    flightId = decodeURIComponent(parts[1] ?? "").trim();
  } catch {
    return null;
  }
  if (!flightId) return null;
  const panel = flightReviewRoutePanels[parts[2] || "overview"] ?? "overview";
  const overviewHealthKey = panel === "overview"
    ? normalizeOverviewHealthCardRouteKey(parts[3])
    : "";
  const route = { flightId, panel };
  if (overviewHealthKey) route.overviewHealthKey = overviewHealthKey;
  const sensitivity = normalizeRouteSensitivity(params.get("sensitivity"));
  if (sensitivity) route.sensitivity = sensitivity;
  const zoom = normalizeRouteZoom(params.get("zoom"));
  if (zoom) route.zoom = zoom;
  if (params.has("channels")) {
    const channels = normalizeRouteChannels(params.get("channels"));
    if (channels) route.channels = channels;
  }
  if (params.has("fit")) {
    const fit = normalizeRouteFit(params.get("fit"));
    if (fit !== null) route.fit = fit;
  }
  return route;
}

export function flightReviewRouteFromLocationHash(location = globalThis.window?.location) {
  return parseFlightReviewHashValue(location?.hash ?? "");
}

export function trendRouteHash(aircraftId, sensitivity = "") {
  const id = String(aircraftId ?? "").trim();
  if (!id) return "";
  return appendRouteStateToHash(`#trends/${encodeURIComponent(id)}`, { sensitivity });
}

export function parseTrendHashValue(value) {
  const { parts, params } = hashValueParts(value);
  if (parts[0] !== "trends" || parts.length < 2) return null;
  let aircraft = "";
  try {
    aircraft = decodeURIComponent(parts[1] ?? "").trim();
  } catch {
    return null;
  }
  if (!aircraft) return null;
  const route = { aircraft };
  const sensitivity = normalizeRouteSensitivity(params.get("sensitivity"));
  if (sensitivity) route.sensitivity = sensitivity;
  return route;
}

export function trendRouteFromLocationHash(location = globalThis.window?.location) {
  return parseTrendHashValue(location?.hash ?? "");
}

export function createViewNavigationController({
  PUBLIC_VIEWS,
  newUiFeatureEnabled = () => false,
  activateAircraftDashboardFlight,
  applyRouteSensitivity,
  clearFlightLogLoading,
  displayMonitorLabelForEntry,
  applyFlightReviewRouteFocus,
  activateTrendRoute,
  ensureAircraftFlightManifestLoaded,
  ensureDetectionGuideRendered,
  ensureFlightDataLoaded,
  findFlightReviewEntry,
  loadFlightLog,
  maybeLoadCachedAiSummaryForSelectedFlight,
  populateTrendAircraftSelect,
  renderAiSummaryPanel,
  renderAircraftWorkspace,
  renderEnginePanel,
  renderFlightProfilePanel,
  renderHealthSummary,
  renderRawDataPanel,
  renderSensitivityControl,
  renderTrendsForSelection,
  scheduleDrawAll,
  setFlightLogLoading,
  showFlightReviewRouteError,
  titleCase,
  updateDataSourceLabel,
  updateFlightHeader,
  updateReportButtonState,
  viewNeedsFlightData,
  selectedAircraftWorkspace,
  getSelectedFlight,
  getSourceMonitor,
  getHealthSummary,
  getSelectedAircraftPanel,
  setSelectedAircraftPanel,
  mountAircraftInfoPanel,
  mountAircraftSettingsPanel,
  syncWorkspaceUrlPath,
}) {
function mountAircraftDashboardPanel() {
  const container = document.querySelector("#aircraftDashboardPanel");
  const reportTabsRow = document.querySelector(".report-tabs-row");
  const dashboardView = document.querySelector("#view-dashboard");
  if (!container || !dashboardView) return;
  if (reportTabsRow && reportTabsRow.parentElement !== container) {
    container.append(reportTabsRow);
  }
  if (dashboardView.parentElement !== container) {
    container.append(dashboardView);
  }
}

// Which aircraft panel was showing last, so "left the Uploads tab" can be told
// apart from "moved between two other tabs".
let lastAircraftPanel = null;

function syncAircraftPanelState(panel) {
  const aircraftPageTabPanels = ["flights", "uploads", "trends", "info", "settings"];
  // NB: leaving the Uploads tab clears its NEW tags, but that is driven from
  // the tab click in setup-navigation, NOT from here. Panel changes also
  // happen for reasons the user did not initiate (the post-upload flow opens
  // the newest flight and then restores a panel), and treating those as "the
  // user looked at Uploads" silently cleared tags for uploads they had never
  // seen. The other dismissal is mousing over a row.
  const hidesAircraftToolbar = ["flights", "uploads", "info", "settings", "trends", "maintenance"].includes(panel);
  document.querySelectorAll("[data-aircraft-panel]").forEach((item) => item.classList.toggle("is-active", item.dataset.aircraftPanel === panel));
  // The aircraft page tab strip (Flights / Uploads / Info / Settings) shows
  // only while one of its panels is active.
  const pageTabs = document.querySelector("#aircraftPageTabs");
  if (pageTabs) pageTabs.hidden = !aircraftPageTabPanels.includes(panel);
  // The topbar "Aircraft" item covers the whole aircraft page, so it stays
  // active on every page tab, not just Flights.
  document.querySelectorAll(".newui-aircraft-nav").forEach((item) => {
    item.classList.toggle("is-active", aircraftPageTabPanels.includes(panel));
  });
  // The trend summary cards belong UNDER the Trends tab, not above it in the
  // aircraft header. They park right below the tab strip (NOT inside
  // #view-trends, whose renderer rewrites its own markup) while Trends is
  // active, and return to their header home elsewhere.
  const activityGrid = document.querySelector("#aircraftActivityGrid");
  const headerPanel = document.querySelector(".aircraft-context-panel");
  if (activityGrid && pageTabs && headerPanel) {
    if (panel === "trends" && activityGrid.previousElementSibling !== pageTabs) pageTabs.after(activityGrid);
    if (panel !== "trends" && activityGrid.parentElement !== headerPanel) headerPanel.append(activityGrid);
  }
  const aircraftContextPanel = document.querySelector(".aircraft-context-panel");
  aircraftContextPanel?.classList.toggle("is-compact", ["flights", "uploads", "info", "settings", "trends", "maintenance"].includes(panel));
  aircraftContextPanel?.classList.toggle("is-dashboard-context", panel === "dashboard");
  aircraftContextPanel?.classList.toggle("is-flights-context", panel === "flights");
  aircraftContextPanel?.classList.toggle("is-trends-context", panel === "trends");
  // Maintenance keeps the same mobile hero treatment as Flights (photo beside
  // the details) — the two pages' heroes must look identical.
  aircraftContextPanel?.classList.toggle("is-maintenance-context", panel === "maintenance");
  aircraftContextPanel?.classList.toggle("is-activity-hidden", panel !== "profile" && panel !== "flights" && panel !== "uploads" && panel !== "trends");
  aircraftContextPanel?.classList.toggle("is-toolbar-hidden", hidesAircraftToolbar);
  document.querySelector("#aircraftDashboardPanel")?.classList.toggle("is-active", panel === "dashboard");
  document.querySelector("#aircraftProfilePanel")?.classList.toggle("is-active", panel === "profile");
  document.querySelector("#aircraftFlightsPanel")?.classList.toggle("is-active", panel === "flights");
  document.querySelector("#aircraftUploadsPanel")?.classList.toggle("is-active", panel === "uploads");
  document.querySelector("#aircraftInfoPanel")?.classList.toggle("is-active", panel === "info");
  document.querySelector("#aircraftSettingsPanel")?.classList.toggle("is-active", panel === "settings");
  document.querySelector("#view-trends")?.classList.toggle("is-active", panel === "trends");
  document.querySelector("#aircraftMaintenancePanel")?.classList.toggle("is-active", panel === "maintenance");
  // The strip is re-shown here, so repaint the dots onto whichever tabs exist.
  syncAircraftTabNewDots();
}

function activeReportPanel() {
  return document.querySelector(".tab.is-active")?.dataset?.panel || "overview";
}

function trackCurrentAnalyticsPageView() {
  trackAnalyticsPageView(analyticsPathForAppState({
    view: document.body.dataset.activeView || "home",
    aircraftPanel: getSelectedAircraftPanel(),
    reportPanel: activeReportPanel(),
  }));
}

function activateView(view, options = {}) {
  if (view === "dashboard") mountAircraftDashboardPanel();
  document.body.dataset.activeView = view;
  // Keep the tail-number path segment scoped to workspace views (Admin, My
  // Account, and public pages drop it).
  syncWorkspaceUrlPath?.();
  // Data views need the flight manifest/analysis; kick off the (idempotent) load on demand so
  // public pages like Home never pay for it. Fire-and-forget: the load path re-renders when done.
  if (viewNeedsFlightData(view)) ensureFlightDataLoaded().catch(() => {});
  const activeNavView = view === "dashboard" ? "aircraft" : view;
  document.querySelectorAll(".nav-item").forEach((item) => item.classList.toggle("is-active", item.dataset.view === activeNavView));
  document.querySelectorAll(".view").forEach((panel) => panel.classList.remove("is-active"));
  const target = document.querySelector(view === "dashboard" ? "#view-aircraft" : `#view-${view}`) || document.querySelector("#view-generic");
  target?.classList.add("is-active");
  if (view === "dashboard") {
    setSelectedAircraftPanel("dashboard");
    document.querySelector("#view-dashboard")?.classList.add("is-active");
    syncAircraftPanelState("dashboard");
  }
  const pageTitle = document.querySelector("#page-title");
  const pageSubhead = document.querySelector(".subhead");
  const flightControlRow = document.querySelector(".flight-control-row");
  const hidesAppHeader = view === "aircraft" || view === "dashboard" || PUBLIC_VIEWS.has(view);
  const hidesFlightControls = hidesAppHeader || view === "account";
  if (flightControlRow) {
    flightControlRow.hidden = hidesFlightControls;
  }
  if (pageTitle) pageTitle.hidden = hidesAppHeader;
  if (pageSubhead) pageSubhead.hidden = hidesAppHeader;
  const reportTabsRow = document.querySelector(".report-tabs-row");
  if (reportTabsRow) reportTabsRow.hidden = view !== "dashboard";
  // Default to Overview, but let callers keep or deep-link a specific report
  // tab so flight hops never flash Overview before restoring the tab.
  if (view === "dashboard") activateDashboardReportPanel(options.reportPanel || "overview");
  if (view !== "dashboard") {
    window.AeroInsightPlayback?.leave?.();
  } else if (document.querySelector("#playbackPanel")?.classList.contains("is-active")) {
    window.AeroInsightPlayback?.enter?.();
  }
  const isCompare = view === "comparisons";
  if (isCompare) {
    document.querySelector("#page-title").textContent = "Comparison: Engine Performance";
    document.querySelector(".subhead").textContent = `${getSelectedFlight()?.aircraft ?? "N000XX"} · comparison workspace`;
  } else if (view === "dashboard") {
    updateFlightHeader();
  } else if (view === "aircraft") {
    setAircraftPanel(getSelectedAircraftPanel(), { updateHeader: false }).catch((error) => updateDataSourceLabel(false, error));
  } else if (view === "detection-guide") {
    ensureDetectionGuideRendered();
  } else if (view === "account") {
    document.querySelector("#page-title").textContent = "My Account";
    document.querySelector(".subhead").textContent = "Account settings and preferences";
  } else if (PUBLIC_VIEWS.has(view)) {
    // Public pages are static, so no app workspace rendering is needed.
  } else {
    document.querySelector("#page-title").textContent = titleCase(view);
    document.querySelector(".subhead").textContent = `${getSelectedFlight()?.aircraft ?? "N000XX"} · ${displayMonitorLabelForEntry(getSelectedFlight(), getSourceMonitor())}`;
  }
  updateReportButtonState(isCompare);
  const primaryAction = document.querySelector("#action-primary");
  if (primaryAction) primaryAction.textContent = isCompare ? "Add to Report" : "Add to Compare";
  const genericTitle = document.querySelector("#generic-title");
  if (genericTitle) genericTitle.textContent = titleCase(view);
  trackCurrentAnalyticsPageView();
  scheduleDrawAll();
}

function activateAircraftWorkspaceShell() {
  document.body.dataset.activeView = "aircraft";
  syncWorkspaceUrlPath?.();
  document.querySelectorAll(".nav-item").forEach((item) => item.classList.toggle("is-active", item.dataset.view === "aircraft"));
  document.querySelectorAll(".view").forEach((panel) => panel.classList.remove("is-active"));
  document.querySelector("#view-aircraft")?.classList.add("is-active");
  const flightControlRow = document.querySelector(".flight-control-row");
  const pageTitle = document.querySelector("#page-title");
  const pageSubhead = document.querySelector(".subhead");
  const reportTabsRow = document.querySelector(".report-tabs-row");
  if (flightControlRow) flightControlRow.hidden = true;
  if (pageTitle) pageTitle.hidden = true;
  if (pageSubhead) pageSubhead.hidden = true;
  if (reportTabsRow) reportTabsRow.hidden = true;
  window.AeroInsightPlayback?.leave?.();
  updateReportButtonState(false);
  scheduleDrawAll();
}

// My Account sub-routes: #account/<tab> deep-links a specific section of the
// account page's sidebar ("plans" is accepted as an alias of "plan"), and
// #account/plan/<planKey> additionally opens that plan's change modal.
function accountTabFromHashValue(value) {
  // Every tab in the account sidebar must be listed here, or its hash is not a
  // route: activateHashView falls through, nothing activates the account view,
  // and boot leaves whatever aircraft workspace was already showing — which is
  // how reloading on #account/settings landed on an aircraft's Flights page.
  const match = /^account\/(?:(profile|security|settings|aircraft|data|billing|archive|time-skipping)|plans?(?:\/(free|basic|plus|pro))?)$/
    .exec(String(value ?? ""));
  if (!match) return null;
  if (match[1]) return { tab: match[1], plan: null };
  return { tab: "plan", plan: match[2] ?? null };
}

function accountTabFromLocationHash() {
  return accountTabFromHashValue(window.location.hash.replace(/^#/, "").trim());
}

// Admin sub-routes: #admin/<tab> deep-links a specific admin sidebar tab, or
// one of the Analytics panel's own sub-tabs (the admin page resolves those to
// Analytics + that sub-tab). "ai-usage" is a sub-tab now but stays listed so
// existing links keep working.
const ADMIN_TAB_KEYS = new Set([
  "users", "test-users", "settings", "plan-settings", "analytics", "retained", "information",
  "site-usage", "funnel", "uploads", "errors", "conversions", "retention", "acquisition", "ai-usage",
  "debug",
]);

// Debug panel sub-tabs, addressed only as #admin/debug/<sub-tab>. A key
// missing here makes the route unparseable, so a RELOAD on that sub-tab
// falls through to the default flights view — add new debug sub-tabs here.
const ADMIN_DEBUG_SUB_TAB_KEYS = new Set(["session", "pipeline", "aircraft-images", "aws-scaling", "supabase-scaling"]);

function adminTabFromHashValue(value) {
  const match = /^admin(?:\/([a-z-]+))?(?:\/([a-z-]+))?$/.exec(String(value ?? ""));
  if (!match) return null;
  let tab = match[1] ?? "";
  const subTab = match[2] ?? "";
  if (subTab) {
    // Two segments exist for #admin/analytics/<sub-tab> (the admin page
    // resolves the bare sub-tab name to Analytics + that panel, so hand it
    // the sub-tab) and #admin/debug/<sub-tab> (passed through as tab +
    // subTab because the debug sub-tab names are not unique route keys).
    if (tab === "debug" && ADMIN_DEBUG_SUB_TAB_KEYS.has(subTab)) {
      return { tab: "debug", subTab };
    }
    if (tab !== "analytics" || !ADMIN_TAB_KEYS.has(subTab)) return null;
    tab = subTab;
  }
  if (tab && !ADMIN_TAB_KEYS.has(tab)) return null;
  return { tab: tab || null };
}

function adminTabFromLocationHash() {
  return adminTabFromHashValue(window.location.hash.replace(/^#/, "").trim());
}

function viewFromLocationHash() {
  const hashView = window.location.hash.replace(/^#/, "").trim();
  if (!hashView) return "";
  if (parseFlightReviewHashValue(hashView)) return "dashboard";
  if (parseTrendHashValue(hashView)) return "aircraft";
  if (accountTabFromHashValue(hashView)) return "account";
  if (aircraftPanelFromHashValue(hashView)) return "";
  if (document.querySelector(`#view-${CSS.escape(hashView)}`)) return hashView;
  return "";
}

function activateHashView() {
  const flightReviewRoute = flightReviewRouteFromLocationHash();
  if (flightReviewRoute) {
    applyRouteSensitivity?.(flightReviewRoute);
    activateView("dashboard", { reportPanel: flightReviewRoute.panel });
    activateDashboardReportPanel(flightReviewRoute.panel);
    applyFlightReviewRouteFocus?.(flightReviewRoute);
    activateFlightReviewRoute(flightReviewRoute);
    return;
  }
  const trendRoute = trendRouteFromLocationHash();
  if (trendRoute) {
    applyRouteSensitivity?.(trendRoute);
    activateAircraftWorkspaceShell();
    activateTrendRoute?.(trendRoute);
    return;
  }
  const aircraftPanel = aircraftPanelFromLocationHash();
  if (aircraftPanel) {
    if (aircraftPanel !== "dashboard") activateAircraftWorkspaceShell();
    setAircraftPanel(aircraftPanel).catch((error) => updateDataSourceLabel(false, error));
    return;
  }
  const accountTab = accountTabFromLocationHash();
  if (accountTab) {
    activateView("account");
    // auth-ui owns the tab panels; it listens for this and flips them.
    window.dispatchEvent(new CustomEvent("aeroinsight:account-tab", {
      detail: { tab: accountTab.tab, plan: accountTab.plan },
    }));
    return;
  }
  const adminTab = adminTabFromLocationHash();
  if (adminTab) {
    activateView("admin");
    // admin-page owns the sidebar tabs; it listens for this and flips them.
    window.dispatchEvent(new CustomEvent("aeroinsight:admin-tab", { detail: { tab: adminTab.tab, subTab: adminTab.subTab ?? null } }));
    return;
  }
  const hashView = viewFromLocationHash();
  if (hashView) {
    activateView(hashView);
    return;
  }
  // Bare URL (no hash) — for signed-in approved users this should land on the
  // aircraft workspace so their fleet/flights populate. Mirrors what direct
  // navigation to `#aircraft` does. Unauthenticated/pending users are still
  // covered by the auth landing / approval gate overlays which sit above the
  // app shell.
  const rawHash = window.location.hash.replace(/^#/, "").trim();
  if (!rawHash) {
    activateAircraftWorkspaceShell();
    setAircraftPanel("profile").catch((error) => updateDataSourceLabel(false, error));
    return;
  }
  trackCurrentAnalyticsPageView();
}

function aircraftPanelFromHashValue(value) {
  const hashView = String(value ?? "").trim();
  if (hashView === "aircraft" || hashView === "profile") return "profile";
  if (hashView === "dashboard") return "dashboard";
  if (hashView === "flights") return "flights";
  if (hashView === "trends") return "trends";
  if (hashView === "maintenance") return "maintenance";
  if (hashView === "uploads") return "uploads";
  if (hashView === "info") return "info";
  if (hashView === "settings") return "settings";
  return "";
}

function aircraftPanelFromLocationHash() {
  return aircraftPanelFromHashValue(window.location.hash.replace(/^#/, ""));
}

function reportPanelToggleGroups() {
  return {
    overviewOnly: document.querySelectorAll(".overview-only"),
    aiReviewOnly: document.querySelectorAll(".ai-review-only"),
    sensitivityField: document.querySelectorAll(".sensitivity-field"),
    reviewSensitivity: document.querySelectorAll(".review-sensitivity"),
  };
}

function activateDashboardReportPanel(panel) {
  const nextPanel = normalizeFlightReviewPanel(panel);
  document.querySelectorAll(".tab").forEach((item) => item.classList.toggle("is-active", item.dataset.panel === nextPanel));
  setReportPanel(nextPanel);
  trackCurrentAnalyticsPageView();
}

function activateFlightReviewRoute(route) {
  ensureFlightDataLoaded()
    .then(async () => {
      const activeRoute = flightReviewRouteFromLocationHash();
      if (!activeRoute || activeRoute.flightId !== route.flightId) return;
      applyRouteSensitivity?.(activeRoute);
      const entry = findFlightReviewEntry?.(route.flightId);
      if (!entry) {
        showFlightReviewRouteError?.(route);
        return;
      }
      if (getSelectedFlight()?.id !== entry.id) {
        await loadFlightLog?.(entry, { preserveRoute: true });
      }
      activateDashboardReportPanel(activeRoute.panel);
      applyFlightReviewRouteFocus?.(activeRoute);
    })
    .catch((error) => {
      showFlightReviewRouteError?.(route, error);
    });
}

function setReportPanel(panel) {
  const isOverview = panel === "overview";
  const isAiReview = panel === "ai-review";
  const isEngine = panel === "engine";
  const isExplorer = panel === "explorer";
  const isProfile = panel === "profile";
  const isRaw = panel === "raw";
  const isPlayback = panel === "playback";
  const showSensitivity = isOverview || isPlayback;
  const wasPlayback = document.querySelector("#playbackPanel")?.classList.contains("is-active") ?? false;
  const toggleGroups = reportPanelToggleGroups();
  toggleGroups.overviewOnly.forEach((element) => {
    element.hidden = !isOverview;
  });
  toggleGroups.aiReviewOnly.forEach((element) => {
    element.hidden = !isAiReview;
  });
  toggleGroups.sensitivityField.forEach((element) => {
    element.hidden = !showSensitivity;
  });
  toggleGroups.reviewSensitivity.forEach((element) => {
    element.hidden = !showSensitivity;
  });
  document.querySelector("#enginePanel")?.classList.toggle("is-active", isEngine);
  document.querySelector("#graphExplorerPanel")?.classList.toggle("is-active", isExplorer);
  document.querySelector("#flightProfilePanel")?.classList.toggle("is-active", isProfile);
  document.querySelector("#rawDataPanel")?.classList.toggle("is-active", isRaw);
  document.querySelector("#playbackPanel")?.classList.toggle("is-active", isPlayback);
  if (isOverview && getHealthSummary()) renderHealthSummary(getHealthSummary());
  if (isAiReview) maybeLoadCachedAiSummaryForSelectedFlight();
  if (isAiReview) renderAiSummaryPanel();
  if (showSensitivity) renderSensitivityControl(getHealthSummary());
  if (isEngine) renderEnginePanel();
  if (isProfile) renderFlightProfilePanel();
  if (isRaw) renderRawDataPanel();
  if (wasPlayback && !isPlayback) window.AeroInsightPlayback?.leave?.();
  if (isPlayback) window.AeroInsightPlayback?.enter?.();
  if (isOverview || isExplorer || isProfile || isEngine || isPlayback) scheduleDrawAll();
}

function activateDashboardAiReviewPanel() {
  document.querySelectorAll(".tab").forEach((item) => item.classList.toggle("is-active", item.dataset.panel === "ai-review"));
  setReportPanel("ai-review");
}

async function setAircraftPanel(panel, options = {}) {
  const normalizedPanel = ["trends", "flights", "uploads", "info", "settings", "dashboard", "maintenance"].includes(panel)
    ? panel
    : "profile";
  // The New UI retires the profile panel: its aircraft page is the Flights
  // page (which carries the summary stat cards). Old UI keeps profile.
  const nextPanel = normalizedPanel === "profile" && newUiFeatureEnabled() ? "flights" : normalizedPanel;
  // The section tabs are visible on every signed-in view (Admin, My
  // Account, ...) and are the only way back into the workspace since the
  // "My Aircraft" button was removed. When a non-dashboard panel is selected
  // from outside the aircraft/dashboard views, swap the workspace shell in
  // BEFORE the panel state sync below (the shell activation resets nav-item
  // highlights, so it must not run after the tab is marked active). The
  // hash-change route does the same, but not every panel writes a hash
  // (trends keeps its own routes) and same-hash clicks never fire it.
  const activeView = document.body.dataset.activeView;
  if (nextPanel !== "dashboard" && activeView !== "aircraft" && activeView !== "dashboard") {
    activateAircraftWorkspaceShell();
  }
  setSelectedAircraftPanel(nextPanel);
  syncAircraftPanelState(nextPanel);
  let loadingToken;
  if (nextPanel === "dashboard") {
    activateView("dashboard", { reportPanel: options.reportPanel });
    loadingToken = setFlightLogLoading(getSelectedFlight() ?? { aircraft: selectedAircraftWorkspace(), title: "Latest available flight" });
  } else {
    trackCurrentAnalyticsPageView();
  }
  // Ensure the flight manifest + aircraft profiles are loaded for every panel (the eager
  // startup load is deferred on public landings, so profile/maintenance/uploads must load it
  // too). This is idempotent and no-ops once the manifest is ready.
  try {
    await perfTime("manifest", () => ensureAircraftFlightManifestLoaded());
  } catch (error) {
    if (nextPanel === "dashboard") await clearFlightLogLoading({ token: loadingToken, loaded: false, message: error?.message });
    throw error;
  }
  if (nextPanel === "dashboard") {
    try {
      await perfTime("activate", () => activateAircraftDashboardFlight());
    } finally {
      await clearFlightLogLoading({ token: loadingToken });
    }
    perfFlush("setAircraftPanel:dashboard");
  } else if (nextPanel === "trends") {
    populateTrendAircraftSelect(selectedAircraftWorkspace() || getSelectedFlight()?.aircraft);
    renderAircraftWorkspace();
    renderTrendsForSelection();
  } else if (nextPanel === "info") {
    renderAircraftWorkspace();
    mountAircraftInfoPanel?.();
  } else if (nextPanel === "settings") {
    renderAircraftWorkspace();
    mountAircraftSettingsPanel?.();
  } else if (options.renderAircraft !== false) {
    renderAircraftWorkspace();
  }
}


  return {
    activateAircraftWorkspaceShell,
    activateDashboardAiReviewPanel,
    activateHashView,
    activateView,
    aircraftPanelFromLocationHash,
    mountAircraftDashboardPanel,
    setAircraftPanel,
    setReportPanel,
    trackCurrentAnalyticsPageView,
    viewFromLocationHash,
  };
}
